import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  CreditCard,
  HelpCircle,
  Info,
  LogIn,
  LogOut,
  Mail,
  Menu,
  Moon,
  Newspaper,
  Shield,
  Sun,
  UserPlus,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';

type MenuLink = { name: string; path: string; icon: LucideIcon };

// Mobile-only top-right menu. Desktop keeps its existing inline navigation (this is `lg:hidden`).
// Destinations mirror the existing public nav (Navbar) and authenticated nav (account dropdown /
// bottom bar) so no new routes are introduced.
const PRIMARY_LINKS: MenuLink[] = [
  { name: 'About us', path: '/about', icon: Info },
  { name: 'Blog', path: '/blog', icon: Newspaper },
  { name: 'Pricing', path: '/pricing', icon: CreditCard },
  { name: 'Contact Us', path: '/contact', icon: Mail },
  { name: 'FAQ', path: '/faqs', icon: HelpCircle },
];

const easeOutExpo = [0.22, 1, 0.36, 1] as const;

export default function MobileMenu() {
  const { isLoggedIn, user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const isAdmin = ['admin', 'super_admin'].includes((user?.role ?? '').toLowerCase());

  const close = () => setOpen(false);

  // Escape closes the menu; lock body scroll while open so the drawer feels native.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const handleSignOut = () => {
    close();
    logout();
    navigate('/');
  };

  return (
    <div className="lg:hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close menu' : 'Open menu'}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls="mobile-menu-panel"
        className="grid h-10 w-10 place-items-center rounded-xl border border-pantheon-teal/50 bg-navy-900 text-white shadow-lg shadow-navy-900/20 transition-colors hover:border-pantheon-teal hover:bg-navy-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-pantheon-teal/70"
      >
        <Menu className="h-5 w-5" />
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="mobile-menu-backdrop"
              className="fixed inset-0 z-[290] bg-navy-950/70 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={close}
              aria-hidden="true"
            />

            <motion.aside
              key="mobile-menu-panel"
              id="mobile-menu-panel"
              role="dialog"
              aria-modal="true"
              aria-label="Navigation menu"
              className="fixed right-0 top-0 z-[300] flex h-[100dvh] w-[82%] max-w-sm flex-col border-l border-white/10 bg-navy-900 shadow-2xl"
              style={{ paddingTop: 'env(safe-area-inset-top)' }}
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.28, ease: easeOutExpo }}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 pb-4 pt-5">
                <Link to="/" onClick={close} className="flex items-center gap-2.5">
                  <img src="/pantheon-mark.png" alt="Pantheon Points" className="h-9 w-9 flex-shrink-0 rounded-full object-cover" />
                  <div className="flex flex-col">
                    <span className="font-display text-sm font-bold uppercase tracking-[0.2em] text-white">
                      Pantheon
                    </span>
                    <span className="text-[8px] font-bold uppercase tracking-[0.3em] text-pantheon-teal">
                      Points
                    </span>
                  </div>
                </Link>
                <button
                  type="button"
                  onClick={close}
                  aria-label="Close menu"
                  className="grid h-9 w-9 place-items-center rounded-full border border-white/10 text-white/70 transition-colors hover:border-pantheon-teal/60 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-pantheon-teal/70"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Links */}
              <nav className="flex-1 overflow-y-auto px-3 pb-2">
                <ul className="space-y-1">
                  {PRIMARY_LINKS.map((link, i) => {
                    const Icon = link.icon;
                    const active = isActive(link.path);
                    return (
                      <motion.li
                        key={link.path}
                        initial={{ opacity: 0, x: 16 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.06 + i * 0.035, duration: 0.25, ease: easeOutExpo }}
                      >
                        <Link
                          to={link.path}
                          onClick={close}
                          aria-current={active ? 'page' : undefined}
                          className={`group flex items-center gap-3 rounded-xl px-3 py-3 transition-colors ${
                            active ? 'bg-pantheon-teal/15 text-pantheon-teal' : 'text-white/80 hover:bg-white/5 hover:text-white'
                          }`}
                        >
                          <span
                            className={`grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg transition-colors ${
                              active
                                ? 'bg-pantheon-teal text-white'
                                : 'bg-white/5 text-white/50 group-hover:bg-pantheon-teal group-hover:text-white'
                            }`}
                          >
                            <Icon className="h-4 w-4" />
                          </span>
                          <span className="min-w-0 flex-1 truncate text-sm font-bold tracking-wide">
                            {link.name}
                          </span>
                        </Link>
                      </motion.li>
                    );
                  })}
                  <motion.li
                    initial={{ opacity: 0, x: 16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.06 + PRIMARY_LINKS.length * 0.035, duration: 0.25, ease: easeOutExpo }}
                  >
                    {/* Theme control (relocated from the bottom banner, into the former Dark Mode slot).
                        Same toggleTheme handler — switches between light and dark. */}
                    <button
                      type="button"
                      onClick={toggleTheme}
                      aria-label="Theme"
                      aria-pressed={theme === 'dark'}
                      className="group flex w-full items-center gap-3 rounded-xl px-3 py-3 text-white/80 transition-colors hover:bg-white/5 hover:text-white"
                    >
                      <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg bg-white/5 text-white/50 transition-colors group-hover:bg-pantheon-teal group-hover:text-white">
                        {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-left text-sm font-bold tracking-wide">
                        Theme
                      </span>
                    </button>
                  </motion.li>
                </ul>
              </nav>

              {/* Footer: account actions */}
              <div
                className="border-t border-white/10 px-3 pt-3"
                style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)' }}
              >
                {isLoggedIn ? (
                  <div className="space-y-2">
                    {isAdmin && (
                      <Link
                        to="/admin"
                        onClick={close}
                        className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-white/80 transition-colors hover:bg-white/5 hover:text-white"
                      >
                        <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg bg-white/5 text-white/50">
                          <Shield className="h-4 w-4" />
                        </span>
                        <span className="min-w-0 flex-1 truncate text-left text-sm font-bold tracking-wide">
                          Admin Panel
                        </span>
                      </Link>
                    )}
                    <button
                      type="button"
                      onClick={handleSignOut}
                      className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-red-400 transition-colors hover:bg-red-500/10"
                    >
                      <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg bg-red-500/10 text-red-400">
                        <LogOut className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1 truncate text-left text-sm font-bold tracking-wide">
                        Sign Out
                      </span>
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2 px-1 pt-1">
                    <Link
                      to="/auth?mode=signin"
                      onClick={close}
                      className="flex items-center justify-center gap-2 rounded-xl bg-pantheon-teal px-4 py-3 text-sm font-bold uppercase tracking-[0.15em] text-white shadow-lg shadow-pantheon-teal/20 transition-colors hover:bg-pantheon-teal-dark"
                    >
                      <LogIn className="h-4 w-4" />
                      Sign In
                    </Link>
                    <Link
                      to="/auth?mode=signup"
                      onClick={close}
                      className="flex items-center justify-center gap-2 rounded-xl border border-white/25 px-4 py-3 text-sm font-bold uppercase tracking-[0.15em] text-white transition-colors hover:bg-white/10"
                    >
                      <UserPlus className="h-4 w-4" />
                      Sign Up
                    </Link>
                  </div>
                )}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
